import React, { useEffect, useMemo, useState } from "react";
import { FiPlus, FiRefreshCw, FiSave, FiTrash2 } from "react-icons/fi";
import {
  createCrmOpportunityPipeline,
  fetchCrmOpportunityPipelines,
  updateCrmOpportunityPipeline,
} from "../crmApi";
import { readStoredUser } from "../../utils/sessionUser";

const DEFAULT_PIPELINE_STAGES = [
  { name: "First outreach", stage_type: "open", color: "#7dd3fc", probability: 10 },
  { name: "Follow-up call", stage_type: "open", color: "#60a5fa", probability: 25 },
  { name: "Needs proposal", stage_type: "open", color: "#a78bfa", probability: 45 },
  { name: "Proposal drafting", stage_type: "open", color: "#f8c571", probability: 60 },
  { name: "Proposal sent", stage_type: "open", color: "#34d399", probability: 75 },
  { name: "Negotiation", stage_type: "open", color: "#fb923c", probability: 85 },
];

const DEFAULT_LEAD_PIPELINE_STAGES = [
  { name: "New lead", stage_type: "open", color: "#7dd3fc", probability: 5 },
  { name: "First contact", stage_type: "open", color: "#60a5fa", probability: 15 },
  { name: "Contacted", stage_type: "open", color: "#a78bfa", probability: 30 },
  { name: "Qualified", stage_type: "open", color: "#34d399", probability: 55 },
  { name: "Ready for opportunity", stage_type: "open", color: "#f8c571", probability: 70 },
];

const PIPELINE_SETTINGS_COPY = {
  title: "Pipelines",
  forbidden: "Your role cannot view pipeline settings.",
  loadError: "Could not load pipelines.",
  saveError: "Could not save pipeline.",
  appliesTo: "Applies to",
  scopes: {
    general: "General",
    lead: "Leads",
    opportunity: "Opportunities",
  },
};

function makeTempId() {
  return `stage-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function getDefaultStages(entityType) {
  return entityType === "lead" ? DEFAULT_LEAD_PIPELINE_STAGES : DEFAULT_PIPELINE_STAGES;
}

function normalizePipelineEntityType(value) {
  return ["general", "lead", "opportunity"].includes(value) ? value : "general";
}

function buildEmptyDraft(entityType = "general") {
  const cleanEntityType = normalizePipelineEntityType(entityType);
  const defaultStages = getDefaultStages(cleanEntityType);
  return {
    id: null,
    name: "",
    entity_type: cleanEntityType,
    status: "active",
    is_default: false,
    sort_order: 0,
    stages: defaultStages.map((stage, index) => ({
      ...stage,
      id: null,
      temp_id: makeTempId(),
      status: "active",
      sort_order: index,
    })),
  };
}

function buildDraft(pipeline) {
  const entityType = normalizePipelineEntityType(pipeline?.entity_type);
  const defaultStages = getDefaultStages(entityType);
  if (!pipeline) return buildEmptyDraft();
  return {
    id: pipeline.id || null,
    name: pipeline.name || "",
    entity_type: entityType,
    status: pipeline.status || "active",
    is_default: Boolean(pipeline.is_default),
    sort_order: Number(pipeline.sort_order || 0),
    stages: (pipeline.stages?.length ? pipeline.stages : defaultStages).map((stage, index) => ({
      id: stage.id || null,
      temp_id: stage.id ? `stage-${stage.id}` : makeTempId(),
      name: stage.name || "",
      stage_type: stage.stage_type || "open",
      color: stage.color || defaultStages[index]?.color || "#7dd3fc",
      probability: stage.probability ?? "",
      status: stage.status || "active",
      sort_order: Number(stage.sort_order ?? index),
    })),
  };
}

function userCanManagePipelines(user) {
  const permissions = user?.permissions || {};
  return (
    Number(user?.id) === 1 ||
    Boolean(permissions.access_settings) ||
    Boolean(permissions["crm.settings.update"]) ||
    Boolean(permissions["crm.pipelines.update"])
  );
}

function userCanViewPipelines(user) {
  const permissions = user?.permissions || {};
  return userCanManagePipelines(user) || Boolean(permissions["crm.settings.view"]) || Boolean(permissions["crm.pipelines.view"]);
}

export default function OpportunityPipelineSettings() {
  const settingsCopy = PIPELINE_SETTINGS_COPY;
  const [user, setUser] = useState(() => readStoredUser());
  const [pipelines, setPipelines] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [draft, setDraft] = useState(() => buildEmptyDraft());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const canView = useMemo(() => userCanViewPipelines(user), [user]);
  const canManage = useMemo(() => userCanManagePipelines(user), [user]);

  useEffect(() => {
    const syncUser = () => setUser(readStoredUser());
    window.addEventListener("palantir-session-updated", syncUser);
    return () => window.removeEventListener("palantir-session-updated", syncUser);
  }, []);

  const selectPipeline = (pipeline) => {
    setSelectedId(pipeline?.id ? String(pipeline.id) : "");
    setDraft(buildDraft(pipeline));
    setError("");
  };

  const loadPipelines = async () => {
    if (!canView) return;
    setLoading(true);
    setError("");
    try {
      const data = await fetchCrmOpportunityPipelines({ include_inactive: 1 });
      const rows = Array.isArray(data?.rows) ? data.rows : [];
      setPipelines(rows);
      const selected = rows.find((item) => String(item.id) === String(selectedId)) || rows[0] || null;
      setSelectedId(selected?.id ? String(selected.id) : "");
      setDraft(buildDraft(selected));
    } catch (err) {
      console.error("[CRM] Opportunity pipelines load error:", err);
      setPipelines([]);
      setError(err.response?.data?.error || settingsCopy.loadError);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPipelines();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView]);

  const startNewPipeline = () => {
    setSelectedId("");
    setDraft(buildEmptyDraft());
    setError("");
  };

  const updateDraft = (field, value) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
  };

  const updateStage = (index, field, value) => {
    setDraft((prev) => ({
      ...prev,
      stages: prev.stages.map((stage, stageIndex) =>
        stageIndex === index ? { ...stage, [field]: value } : stage
      ),
    }));
  };

  const addStage = () => {
    setDraft((prev) => ({
      ...prev,
      stages: [
        ...prev.stages,
        {
          id: null,
          temp_id: makeTempId(),
          name: "",
          stage_type: "open",
          color: "#7dd3fc",
          probability: "",
          status: "active",
          sort_order: prev.stages.length,
        },
      ],
    }));
  };

  const removeStage = (index) => {
    setDraft((prev) => ({
      ...prev,
      stages: prev.stages
        .filter((_, stageIndex) => stageIndex !== index)
        .map((stage, stageIndex) => ({ ...stage, sort_order: stageIndex })),
    }));
  };

  const savePipeline = async () => {
    if (!canManage || saving) return;
    if (!String(draft.name || "").trim()) {
      setError("Pipeline name is required.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      const payload = {
        name: draft.name,
        entity_type: normalizePipelineEntityType(draft.entity_type),
        status: draft.status,
        is_default: Boolean(draft.is_default),
        sort_order: Number(draft.sort_order || 0),
        stages: draft.stages.map((stage, index) => ({
          id: stage.id || undefined,
          name: stage.name,
          stage_type: stage.stage_type,
          color: stage.color || null,
          probability: stage.probability === "" ? null : Number(stage.probability),
          status: stage.status || "active",
          sort_order: Number(stage.sort_order ?? index),
        })),
      };

      const saved = draft.id
        ? await updateCrmOpportunityPipeline(draft.id, payload)
        : await createCrmOpportunityPipeline(payload);

      const data = await fetchCrmOpportunityPipelines({ include_inactive: 1 });
      const rows = Array.isArray(data?.rows) ? data.rows : [];
      setPipelines(rows);
      selectPipeline(rows.find((item) => Number(item.id) === Number(saved?.id)) || saved);
    } catch (err) {
      console.error("[CRM] Opportunity pipeline save error:", err);
      setError(err.response?.data?.error || settingsCopy.saveError);
    } finally {
      setSaving(false);
    }
  };

  if (!canView) {
    return (
      <main className="crm-page crm-page--pipelineSettings">
        <section className="crm-panel">
          <p className="crm-panelKicker">CRM Settings</p>
          <h1 className="crm-settingsTitle">{settingsCopy.title}</h1>
          <p className="crm-settingsMuted">{settingsCopy.forbidden}</p>
        </section>
      </main>
    );
  }

  return (
    <main className="crm-page crm-page--pipelineSettings">
      <header className="crm-pageHeader">
        <div>
          <p className="crm-eyebrow">CRM Settings</p>
          <h1>{settingsCopy.title}</h1>
        </div>
        <div className="crm-pageHeaderActions">
          <button type="button" className="crm-ghostButton" onClick={loadPipelines} disabled={loading}>
            <FiRefreshCw /> {loading ? "Loading" : "Reload"}
          </button>
          <button type="button" className="crm-primaryButton" onClick={startNewPipeline} disabled={!canManage}>
            <FiPlus /> New pipeline
          </button>
        </div>
      </header>

      {error ? <div className="crm-settingsError">{error}</div> : null}

      <div className="crm-pipelineSettingsGrid">
        <section className="crm-panel crm-pipelineSettingsList">
          <div className="crm-panelHeader">
            <div>
              <p className="crm-panelKicker">Pipelines</p>
              <h2>{pipelines.length} configured</h2>
            </div>
          </div>

          <div className="crm-settingsList">
            {pipelines.length ? (
              pipelines.map((pipeline) => (
                <button
                  key={pipeline.id}
                  type="button"
                  className={`crm-settingsListItem ${
                    String(selectedId) === String(pipeline.id) ? "crm-settingsListItem--active" : ""
                  }`}
                  onClick={() => selectPipeline(pipeline)}
                >
                  <strong>{pipeline.name}</strong>
                  <span>
                    {settingsCopy.scopes?.[normalizePipelineEntityType(pipeline.entity_type)] || settingsCopy.scopes.general}
                    {" · "}
                    {pipeline.is_default ? "Default · " : ""}
                    {(pipeline.stages || []).filter((stage) => stage.status !== "inactive").length} active stages
                  </span>
                  <em className={`crm-statusPill crm-statusPill--${pipeline.status === "inactive" ? "inactive" : "active"}`}>
                    {pipeline.status}
                  </em>
                </button>
              ))
            ) : (
              <div className="crm-tableEmpty">{loading ? "Loading pipelines..." : "No pipelines created yet."}</div>
            )}
          </div>
        </section>

        <section className="crm-panel crm-pipelineSettingsEditor">
          <div className="crm-panelHeader">
            <div>
              <p className="crm-panelKicker">{draft.id ? "Edit pipeline" : "New pipeline"}</p>
              <h2>{draft.name || "Untitled pipeline"}</h2>
            </div>
            <button type="button" className="crm-primaryButton" onClick={savePipeline} disabled={!canManage || saving}>
              <FiSave /> {saving ? "Saving" : "Save"}
            </button>
          </div>

          <div className="crm-formGrid">
            <label className="crm-field crm-field--wide">
              <span>Name</span>
              <input value={draft.name} onChange={(event) => updateDraft("name", event.target.value)} disabled={!canManage} />
            </label>

            <label className="crm-field">
              <span>{settingsCopy.appliesTo}</span>
              <select
                value={normalizePipelineEntityType(draft.entity_type)}
                onChange={(event) => updateDraft("entity_type", event.target.value)}
                disabled={!canManage}
              >
                <option value="general">{settingsCopy.scopes.general}</option>
                <option value="lead">{settingsCopy.scopes.lead}</option>
                <option value="opportunity">{settingsCopy.scopes.opportunity}</option>
              </select>
            </label>

            <label className="crm-field">
              <span>Order</span>
              <input
                type="number"
                value={draft.sort_order}
                onChange={(event) => updateDraft("sort_order", event.target.value)}
                disabled={!canManage}
              />
            </label>

            <label className="crm-field">
              <span>Status</span>
              <select value={draft.status} onChange={(event) => updateDraft("status", event.target.value)} disabled={!canManage}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </label>

            <label className="crm-settingsToggle crm-field--wide">
              <input
                type="checkbox"
                checked={Boolean(draft.is_default)}
                onChange={(event) => updateDraft("is_default", event.target.checked)}
                disabled={!canManage || draft.status === "inactive"}
              />
              <span>Default pipeline for this business unit</span>
            </label>
          </div>

          <div className="crm-settingsStageHeader">
            <div>
              <p className="crm-panelKicker">Stages</p>
              <strong>{draft.stages.length} stages</strong>
            </div>
            <button type="button" className="crm-ghostButton" onClick={addStage} disabled={!canManage}>
              <FiPlus /> Add stage
            </button>
          </div>

          <div className="crm-settingsStageList">
            {draft.stages.map((stage, index) => (
              <div key={stage.id || stage.temp_id} className="crm-settingsStage">
                <label className="crm-field">
                  <span>Stage name</span>
                  <input value={stage.name} onChange={(event) => updateStage(index, "name", event.target.value)} disabled={!canManage} />
                </label>
                <label className="crm-field">
                  <span>Column type</span>
                  <select value={stage.stage_type} onChange={(event) => updateStage(index, "stage_type", event.target.value)} disabled={!canManage}>
                    <option value="open">Open</option>
                    <option value="won">Won</option>
                    <option value="lost">Lost</option>
                  </select>
                </label>
                <label className="crm-field">
                  <span>Probability</span>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={stage.probability}
                    onChange={(event) => updateStage(index, "probability", event.target.value)}
                    disabled={!canManage}
                  />
                </label>
                <label className="crm-field">
                  <span>Order</span>
                  <input type="number" value={stage.sort_order} onChange={(event) => updateStage(index, "sort_order", event.target.value)} disabled={!canManage} />
                </label>
                <label className="crm-field crm-colorField">
                  <span>Color</span>
                  <input type="color" value={stage.color || "#7dd3fc"} onChange={(event) => updateStage(index, "color", event.target.value)} disabled={!canManage} />
                </label>
                <label className="crm-field">
                  <span>Status</span>
                  <select value={stage.status} onChange={(event) => updateStage(index, "status", event.target.value)} disabled={!canManage}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </label>
                <button type="button" className="crm-iconButton crm-settingsRemove" onClick={() => removeStage(index)} disabled={!canManage} title="Remove stage">
                  <FiTrash2 />
                </button>
              </div>
            ))}
          </div>

          {!canManage ? <p className="crm-settingsMuted">You can view pipelines, but your role cannot edit them.</p> : null}
        </section>
      </div>
    </main>
  );
}
